/**
 * Приветствие публичного viewer: до открытия модели вместо пустой сцены.
 * Во встроенной сборке СОД не показывается.
 */
import { dom } from "../dom";
import {
  copyPresentationLink,
  currentPresentationId,
  openPresentationFile,
  PRESENTATION_FILES,
  presentationThumbnailPath,
} from "../ifc/presentationFiles";
import { escapeHtmlAttr, escapeHtmlText } from "../utils/html";
import { getThemePreference, setThemePreference, type ThemePreference } from "./theme";

const PUBLIC = import.meta.env.VITE_ANALYTICS_MODE === "public";

let parkedWelcome: HTMLElement | null = null;

function welcomeRoot(): HTMLElement | null {
  return document.getElementById("public-welcome") || parkedWelcome;
}

function appRoot(): HTMLElement | null {
  return document.getElementById("app");
}

function clearWelcomeMode(): void {
  const app = appRoot();
  app?.classList.remove("welcome-boot", "welcome-open");
  document.body.classList.remove("welcome-open");
  document.documentElement.classList.remove("welcome-boot");
}

export function hidePublicWelcome(): void {
  const root = welcomeRoot();
  clearWelcomeMode();
  if (!root) {
    return;
  }
  root.hidden = true;
  parkedWelcome = root;
  root.remove();
}

export function showPublicWelcome(): void {
  // Публичный viewer открывает инструмент сразу, без экрана приветствия.
  // Только снимаем режим приветствия (классы welcome-boot/welcome-open),
  // чтобы боковая панель и вкладки оставались видимыми при пустой сцене
  // и после выгрузки модели. Встроенная в СОД сборка сюда не попадает:
  // в ней VITE_ANALYTICS_MODE=embedded и вызовов showPublicWelcome нет.
  clearWelcomeMode();
}

function renderExamples(host: HTMLElement): void {
  host.innerHTML = PRESENTATION_FILES.map((pf) => {
    const id = escapeHtmlAttr(pf.id);
    const title = escapeHtmlText(pf.title);
    const titleAttr = escapeHtmlAttr(pf.title);
    return (
      `<article class="welcome-example">` +
      `<button type="button" class="welcome-example-open" data-presentation-id="${id}">` +
      `<img class="welcome-example-thumb" src="${escapeHtmlAttr(presentationThumbnailPath(pf))}" alt="" />` +
      `<span class="welcome-example-title">${title}</span>` +
      `</button>` +
      `<span class="welcome-example-meta">` +
      `<span>${escapeHtmlText(pf.sizeLabel)}</span>` +
      `<button type="button" class="ifc-presentation-copy" data-presentation-copy="${id}" title="Скопировать ссылку" aria-label="Скопировать ссылку на «${titleAttr}»">` +
      `<svg viewBox="0 0 16 16" aria-hidden="true" width="14" height="14"><rect x="5" y="5" width="8" height="8" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="M4 11H3.5A1.5 1.5 0 0 1 2 9.5v-6A1.5 1.5 0 0 1 3.5 2h6A1.5 1.5 0 0 1 11 3.5V4" fill="none" stroke="currentColor" stroke-width="1.4"/></svg>` +
      `</button>` +
      `</span>` +
      `</article>`
    );
  }).join("");
}

function syncThemeToggle(): void {
  const wrap = document.getElementById("welcome-theme-switch");
  if (!wrap) {
    return;
  }
  const pref = getThemePreference();
  wrap.dataset.pref = pref;
  wrap.querySelectorAll<HTMLButtonElement>("[data-theme-pref]").forEach((button) => {
    const selected = button.dataset.themePref === pref;
    button.setAttribute("aria-checked", selected ? "true" : "false");
    button.tabIndex = selected ? 0 : -1;
  });
}

function chooseWelcomeTheme(next: ThemePreference): void {
  setThemePreference(next);
  dom.settingsThemeSelect.value = next;
  syncThemeToggle();
}

export function initPublicWelcome(): void {
  const root = welcomeRoot();
  if (!root || !PUBLIC) {
    clearWelcomeMode();
    return;
  }
  syncThemeToggle();
  window.addEventListener("ifc-viewer-theme", () => {
    const examples = welcomeRoot()?.querySelector<HTMLElement>("#welcome-examples");
    if (examples) {
      renderExamples(examples);
    }
    syncThemeToggle();
  });
  root.querySelector("#welcome-theme-switch")?.addEventListener("click", (event) => {
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }
    const button = target.closest<HTMLButtonElement>("[data-theme-pref]");
    const pref = button?.dataset.themePref;
    if (pref === "dark" || pref === "light" || pref === "auto") {
      chooseWelcomeTheme(pref);
    }
  });
  const examples = root.querySelector<HTMLElement>("#welcome-examples");
  if (examples) {
    renderExamples(examples);
  }
  root.querySelector<HTMLButtonElement>("#welcome-open-file")?.addEventListener("click", () => {
    dom.fileInput.click();
  });
  examples?.addEventListener("click", (event) => {
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }
    const copyBtn = target.closest<HTMLElement>("[data-presentation-copy]");
    if (copyBtn?.dataset.presentationCopy) {
      event.preventDefault();
      event.stopPropagation();
      void copyPresentationLink(copyBtn.dataset.presentationCopy, copyBtn);
      return;
    }
    const openBtn = target.closest<HTMLElement>("[data-presentation-id]");
    const id = openBtn?.dataset.presentationId;
    if (id) {
      void openPresentationFile(id);
    }
  });
  const params = new URLSearchParams(window.location.search);
  if (currentPresentationId() || params.get("fileId") || params.get("src")) {
    hidePublicWelcome();
    return;
  }
  showPublicWelcome();
  appRoot()?.classList.remove("welcome-boot");
  document.documentElement.classList.remove("welcome-boot");
}
